import { useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: false
  }
});

const VisitorTracker = () => {
  useEffect(() => {
    const trackVisitor = async () => {
      try {
        // Check if this visitor already has a session ID stored
        let sessionId = localStorage.getItem('visitor_session_id');
        
        // If not, create a new one
        if (!sessionId) {
          sessionId = uuidv4();
          localStorage.setItem('visitor_session_id', sessionId);
        }
        
        // Collect visitor data
        const visitorData = {
          session_id: sessionId,
          user_agent: navigator.userAgent,
          referrer: document.referrer || null,
          screen_size: `${window.screen.width}x${window.screen.height}`,
          visit_timestamp: new Date().toISOString(),
          region: 'unknown',
          ip_address: 'unknown',
          country: 'unknown',
          city: 'unknown',
          total_visits: 1
        };
        
        // Step 1: Get the IP address
        try {
          console.log("Fetching IP address...");
          const ipResponse = await fetch('https://api.ipify.org?format=json');
          if (ipResponse.ok) {
            const ipData = await ipResponse.json();
            visitorData.ip_address = ipData.ip;
            console.log("IP address obtained:", ipData.ip);
            
            // Step 2: Now use Supabase Edge Function to get location data
            try {
              console.log("Calling Edge Function with IP:", ipData.ip);
              const { data: locationData, error: fnError } = await supabase.functions.invoke('get-location', {
                body: { ip: ipData.ip }
              });
              
              console.log("Edge function response:", locationData);
              
              if (fnError) {
                console.error("Edge function error:", fnError);
              } else if (locationData && locationData.success) {
                visitorData.country = locationData.country;
                visitorData.city = locationData.city;
                visitorData.region = locationData.region;
                console.log("Location data retrieved:", locationData);
              } else {
                console.warn("Edge function didn't return expected data:", locationData);
              }
            } catch (edgeError) {
              console.error("Error calling Edge Function:", edgeError);
            }
          }
        } catch (locationError) {
          console.error('Could not fetch IP address:', locationError);
        }
        
        // Handle database operations
        try {
          console.log("Checking database for existing sessions...");
          const { data: existingSessions } = await supabase
            .from('site_visitors')
            .select('*')
            .eq('session_id', sessionId);
          
          if (existingSessions && existingSessions.length > 0) {
            console.log("Found existing sessions:", existingSessions.length);
            // Keep the newest record, remove all others
            const sortedSessions = existingSessions.sort((a, b) => 
              new Date(b.visit_timestamp) - new Date(a.visit_timestamp)
            );
            
            const newest = sortedSessions[0];
            
            // Delete any duplicates
            if (sortedSessions.length > 1) {
              for (let i = 1; i < sortedSessions.length; i++) {
                await supabase
                  .from('site_visitors')
                  .delete()
                  .eq('id', sortedSessions[i].id);
              }
              console.log(`Cleaned up ${sortedSessions.length - 1} duplicate session records`);
            }
            
            // Update the remaining record - now including region
            const { error: updateError } = await supabase
              .from('site_visitors')
              .update({ 
                visit_timestamp: new Date().toISOString(),
                total_visits: (newest.total_visits || 0) + 1,
                ip_address: visitorData.ip_address,
                country: visitorData.country,
                city: visitorData.city,
                region: visitorData.region  // Added region to the update
              })
              .eq('id', newest.id);
              
            if (updateError) {
              console.error('Error updating visitor session:', updateError);
            } else {
              console.log(`Visitor session updated, total visits: ${(newest.total_visits || 0) + 1}`);
              console.log(`Location data: ${visitorData.ip_address}, ${visitorData.country}, ${visitorData.city}, ${visitorData.region}`);  // Updated log to include region
            }
          } else {
            console.log("No existing sessions found, creating new record");
            // No existing sessions, create a new one
            const { error: insertError } = await supabase
              .from('site_visitors')
              .insert([visitorData]);  // visitorData already includes the region field
              
            if (insertError) {
              console.error('Error tracking visitor:', insertError);
            } else {
              console.log('New visitor tracked successfully');
              console.log(`Location data: ${visitorData.ip_address}, ${visitorData.country}, ${visitorData.city}, ${visitorData.region}`);  // Updated log to include region
            }
          }
        } catch (dbError) {
          console.error('Database operation failed:', dbError);
        }
      } catch (error) {
        console.error('Visitor tracking failed:', error);
      }
    };
    
    trackVisitor();
  }, []);
  
  return null;
};

export default VisitorTracker;